import type { ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { fetchContent } from './api'
import { ContentContext } from './ContentContext'
import { defaultContent } from './defaultContent'
import type { SiteContent } from './schema'

interface ContentProviderProps {
  children: ReactNode
}

/**
 * Supplies site content, preferring the API and falling back to the compiled
 * defaults.
 *
 * Merging rules, per section:
 *  - a section the admin has never touched keeps its default, so new sections
 *    added in code appear immediately without a migration;
 *  - arrays replace wholesale, because removing an item has to be possible;
 *  - plain objects merge field by field over the default, so a partial
 *    document cannot blank fields it simply did not mention. Without this a
 *    stored `{ site: { name } }` would erase the tagline, portrait and every
 *    other identity field.
 */
const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

export const ContentProvider = ({ children }: ContentProviderProps) => {
  const [overrides, setOverrides] = useState<Partial<SiteContent> | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    let active = true
    fetchContent()
      .then((data) => {
        if (active) setOverrides(data)
      })
      .finally(() => {
        if (active) setIsLoaded(true)
      })
    return () => {
      active = false
    }
  }, [])

  /**
   * Re-reads the stored overrides.
   *
   * The admin panel calls this after a save, so an edit is visible on the
   * live site in the same breath rather than after a reload. Cheap: the
   * document is one small JSON row.
   */
  const refresh = useCallback(async () => {
    const data = await fetchContent()
    if (data) setOverrides(data)
  }, [])

  const value = useMemo(() => {
    const content: SiteContent = { ...defaultContent }
    if (overrides) {
      for (const [key, stored] of Object.entries(overrides)) {
        // Ignore null/undefined so a partial document cannot erase a section.
        if (stored === null || stored === undefined) continue

        const fallback = (defaultContent as unknown as Record<string, unknown>)[
          key
        ]
        const value: unknown =
          isPlainObject(stored) && isPlainObject(fallback)
            ? { ...fallback, ...(stored as Record<string, unknown>) }
            : stored
        Object.assign(content, { [key]: value })
      }
    }
    return { content, isLoaded, refresh }
  }, [overrides, isLoaded, refresh])

  return (
    <ContentContext.Provider value={value}>{children}</ContentContext.Provider>
  )
}
